import { Component, OnDestroy, ViewChild, ElementRef, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { FormGroup, Validators, FormControl, ValidatorFn, AbstractControl } from '@angular/forms';
import { FamilyLinkType } from '@bciti/types';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserRecord, PostFamilyLinkPayload } from '../../user.model';
import { takeUntil } from 'rxjs/operators';
import { formatDateOnly } from 'src/app/global/utils/date/date.utils';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { UserService } from '../../service/user.abstract.service';

export interface UploadOptions {
    multiple: boolean;
    restrictionMessage: string;
    allowedFileTypes: string[];
    maxFileSize: number;
    autoComplete: boolean;
}

const LINK_CHILD = 'CHILD';
const LINK_PARENT = 'PARENT';
const LINK_OTHER = 'OTHER';
const LINK_SPOUSE = 'SPOUSE';

@Component({
    selector: 'add-family-member-dialog',
    templateUrl: './add-family-member-dialog.component.html',
    styleUrls: ['./add-family-member-dialog.component.scss'],
})
export class AddFamilyMemberDialogComponent implements OnDestroy {
    destroy$ = new Subject();
    @ViewChild('fileDropRef', { static: false }) fileDropEl: ElementRef<HTMLInputElement>;

    private t: any;
    allCoreConfig: any;
    instructions: string;

    processingSubmit = false;

    numFilesLoading = 0;
    numFilesLoaded = 0;

    minimumProofOfIdentity = 0;
    tooManyFiles = false;
    maxDate = new Date();

    get options(): UploadOptions {
        return {
            multiple: true,
            autoComplete: false,
            allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
            restrictionMessage: this.translateService.instant('pages.userFamily.create.attachments.restrictions'),
            maxFileSize: 20000000,
        };
    }

    addFamilyMemberForm: any = new FormGroup({
        firstName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [Validators.required, Validators.minLength(2)]),
        birthDate: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.email, this.forbiddenSameEmailValidator(this.data?.userRecord?.email)]),
        link: new FormControl('', [Validators.required]),
        reference: new FormControl('', [Validators.required]),
    });

    familyLinks = Object.values(FamilyLinkType);

    constructor(
        private translateService: TranslateService,
        private dialogRef: MatDialogRef<AddFamilyMemberDialogComponent>,
        private userService: UserService,
        @Inject(MAT_DIALOG_DATA) public data: { userRecord: UserRecord },
        private messageService: MessageService,
    ) {}

    /**
     * Close the current dialog.
     */
    public closeDialog() {
        this.dialogRef.close();
    }

    /**
     * Send the family link request.
     */
    public submit() {
        this.processingSubmit = true;
        const payload: PostFamilyLinkPayload = {
            ...this.addFamilyMemberForm.value,
            birthDate: formatDateOnly(this.addFamilyMemberForm.value.birthDate),
        };

        this.userService
            .addFamilyLink(this.data.userRecord.id, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translateService.instant('pages.userFamily.create.messages.success'));
                    this.processingSubmit = false;
                    this.dialogRef.close(true);
                },
                (error) => {
                    this.processingSubmit = false;
                    this.messageService.error(this.translateService.instant(error?.error?.messages?.[0], 'pages.userFamily.create.messages.error'));
                },
            );
    }

    /**
     * Change the value of the link type (Children, Sibling, etc)
     * @param e The new link
     */
    public linkChange(e) {
        this.addFamilyMemberForm.patchValue({ link: e });

        switch (e.toUpperCase()) {
            case LINK_CHILD:
                this.addFamilyMemberForm.patchValue({ reference: LINK_PARENT });
                break;
            case LINK_PARENT:
                this.addFamilyMemberForm.patchValue({ reference: LINK_CHILD });
                break;
            case LINK_OTHER:
                this.addFamilyMemberForm.patchValue({ reference: LINK_OTHER });
                break;
            case LINK_SPOUSE:
                this.addFamilyMemberForm.patchValue({ reference: LINK_SPOUSE });
                break;
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private forbiddenSameEmailValidator(email: string): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            const forbidden = email === control.value;
            return forbidden ? { sameEmail: 'error' } : null;
        };
    }

    /**
     * GETTERS
     */
    get firstName() {
        return this.addFamilyMemberForm.get('firstName');
    }
    get lastName() {
        return this.addFamilyMemberForm.get('lastName');
    }
    get email() {
        return this.addFamilyMemberForm.get('email');
    }
    get link() {
        return this.addFamilyMemberForm.get('link');
    }
    get reference() {
        return this.addFamilyMemberForm.get('reference');
    }
    get birthDate() {
        return this.addFamilyMemberForm.get('birthDate');
    }
}
